import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { Loading } from "../../components/shared";
import VendorAgreementGate from "./VendorAgreementGate";
import {
  Building2,
  CheckCircle2,
  FileText,
  MapPinned,
  ShieldCheck,
  Wrench,
} from "lucide-react";

const STATUS_STYLES = {
  ACTIVE: "bg-emerald-100 text-emerald-800",
  VERIFIED: "bg-emerald-100 text-emerald-800",
  WAIVED: "bg-sky-100 text-sky-800",
  EXPIRED: "bg-amber-100 text-amber-800",
  REVIEW_REQUIRED: "bg-amber-100 text-amber-800",
  NOT_STARTED: "bg-slate-100 text-slate-700",
};

function Status({ label, value, testId }) {
  const style = STATUS_STYLES[value] || "bg-slate-100 text-slate-700";
  return (
    <div className="border border-slate-200 bg-white p-5" data-testid={testId}>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <span className={`mt-3 inline-flex px-2.5 py-1 text-sm font-semibold ${style}`}>
        {String(value || "Not available").replaceAll("_", " ")}
      </span>
    </div>
  );
}

export default function VendorHome() {
  const [dashboard, setDashboard] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/vendor/dashboard")
      .then((response) => setDashboard(response.data))
      .catch((requestError) => setError(requestError.response?.data?.detail || "Unable to load portal"));
  }, []);

  if (!dashboard && !error) return <Loading />;
  if (error) {
    return (
      <div className="border border-amber-200 bg-amber-50 p-6" data-testid="vendor-dashboard-error">
        <h1 className="font-display text-2xl font-bold">Vendor Portal unavailable</h1>
        <p className="mt-2 text-sm text-amber-900">{error}</p>
      </div>
    );
  }

  const vendor = dashboard.vendor_context;
  const subscription = dashboard.subscription || {};
  const actions = [
    ["Complete profile", "/vendor/profile", Building2, "vendor-dashboard-profile-action"],
    ["Manage services", "/vendor/services", Wrench, "vendor-dashboard-services-action"],
    ["Manage service areas", "/vendor/service-areas", MapPinned, "vendor-dashboard-areas-action"],
    ["Upload documents", "/vendor/documents", FileText, "vendor-dashboard-documents-action"],
  ];

  return (
    <div className="space-y-8">
      <VendorAgreementGate />
      <section className="border-l-4 border-emerald-600 bg-white px-6 py-7 sm:px-8" data-testid="vendor-home-header">
        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Vendor Portal</p>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-5">
          <div>
            <h1 className="font-display text-3xl font-bold sm:text-4xl">{vendor.business_name}</h1>
            <p className="mt-2 text-sm text-slate-600" data-testid="vendor-display-id">
              Vendor ID: {vendor.vendor_display_id}
            </p>
          </div>
          <div className="border border-slate-200 px-4 py-3" data-testid="vendor-profile-completion-card">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Profile completion</p>
            <p className="mt-1 text-2xl font-bold text-emerald-800">
              {dashboard.profile_completion.percentage}%
            </p>
          </div>
        </div>
      </section>

      <section className="grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 xl:grid-cols-4">
        <Status label="Verification" testId="vendor-verification-status" value={dashboard.verification} />
        <Status label="Account" testId="vendor-account-status" value={dashboard.account_status} />
        <Status
          label="Subscription"
          testId="vendor-subscription-status"
          value={subscription.effective_status}
        />
        <div className="bg-white p-5" data-testid="vendor-document-status">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Documents</p>
          <p className="mt-3 text-2xl font-bold">{dashboard.document_summary.total}</p>
          <p className="mt-1 text-xs text-slate-500">
            {dashboard.document_summary.verified} verified · {dashboard.document_summary.pending} pending
          </p>
        </div>
      </section>

      {dashboard.profile_completion.missing.length > 0 && (
        <section className="border border-amber-200 bg-amber-50 p-5" data-testid="vendor-profile-incomplete-banner">
          <div className="flex gap-3">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-amber-800" />
            <div>
              <h2 className="font-semibold text-amber-950">Complete your business profile</h2>
              <p className="mt-1 text-sm text-amber-900">
                Still needed: {dashboard.profile_completion.missing.join(", ")}.
              </p>
            </div>
          </div>
        </section>
      )}

      <section>
        <div className="mb-4 flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-emerald-700" />
          <h2 className="font-display text-xl font-bold">Keep your business ready</h2>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {actions.map(([label, path, Icon, testId]) => (
            <Link
              className="group border border-slate-200 bg-white p-5 transition-transform hover:-translate-y-0.5 hover:border-emerald-500"
              data-testid={testId}
              key={path}
              to={path}
            >
              <Icon className="h-5 w-5 text-emerald-700" />
              <p className="mt-8 font-semibold">{label}</p>
              <p className="mt-1 text-sm text-slate-500">Update the information MySocietyMitra uses to review you.</p>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}